import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { z } from 'zod'
import { useBreadcrumbFromRoute } from '@/hooks/use-breadcrumb-from-route'
import { useDebouncedValue } from '@/hooks/use-debounced-value'
import { Button } from '@/components/ui/button'
import { InvoicesTable } from '@/components/invoices/invoices-table'
import { Badge } from '@/components/ui/badge'
import { AlertTriangle, Plus } from 'lucide-react'
import { InvoiceStatusEnum } from '@/gen/cortado/v1/settings_pb'
import { useInfiniteInvoicesList, useInvoiceStats } from '@/stores/invoices.store'
import { useDisputesList } from '@/stores/disputes.store'
import { LoadMoreBar } from '@/components/shared/load-more-bar'
import { LoadingOverlay } from '@/components/shared/loading'
import { useStore } from '@tanstack/react-store'
import { organizationStore } from '@/stores/organization.store'
import { useStripeConnection } from '@/stores/stripe.store'
import { DocumentStatCard } from '@/components/shared/financial-document'
import { ActionGate } from '@/components/shared/action-gate'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'

const invoicesSearchSchema = z.object({
  tab: z.enum(['open', 'overdue', 'paid', 'cancelled']).optional(),
})

type InvoicesTab = 'open' | 'overdue' | 'paid' | 'cancelled'

const TAB_STATUSES: Record<InvoicesTab, InvoiceStatusEnum[]> = {
  open: [
    InvoiceStatusEnum.INVOICE_STATUS_DRAFT,
    InvoiceStatusEnum.INVOICE_STATUS_SENT,
    InvoiceStatusEnum.INVOICE_STATUS_VIEWED,
    InvoiceStatusEnum.INVOICE_STATUS_PAYMENT_PENDING,
    InvoiceStatusEnum.INVOICE_STATUS_PARTIALLY_PAID,
  ],
  overdue: [InvoiceStatusEnum.INVOICE_STATUS_OVERDUE],
  paid: [
    InvoiceStatusEnum.INVOICE_STATUS_PAID,
    InvoiceStatusEnum.INVOICE_STATUS_OVERPAID,
  ],
  cancelled: [InvoiceStatusEnum.INVOICE_STATUS_CANCELLED],
}

function DisputesBadge() {
  const { needsResponseCount, isLoading, error } = useDisputesList()
  if (isLoading || error) return null
  if (!needsResponseCount || needsResponseCount <= 0) return null

  return (
    <Link to="/disputes">
      <Badge variant="destructive">
        <AlertTriangle className="h-3 w-3" />
        {needsResponseCount} {needsResponseCount === 1 ? 'dispute needs' : 'disputes need'} response
      </Badge>
    </Link>
  )
}

// Degraded stat card shown when the stats RPC errors. The stat cards double as
// the tab buttons, so they must stay rendered and clickable even without data —
// mirrors DocumentStatCard's layout with em-dash placeholder values.
function UnavailableStatCard({
  label,
  isActive,
  onClick,
}: {
  label: string
  isActive?: boolean
  onClick?: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded-lg border bg-card p-4 text-left transition-colors hover:bg-accent/50',
        isActive && 'border-primary bg-primary/5 ring-1 ring-primary',
      )}
    >
      <p className="font-financial text-2xl font-bold">—</p>
      <p className="mt-2 font-medium">{label}</p>
      <p className="mt-1 text-sm text-muted-foreground">—</p>
    </button>
  )
}

export const Route = createFileRoute('/_app/_home/invoices/')({
  component: InvoicesPage,
  validateSearch: invoicesSearchSchema,
})

function InvoicesPage() {
  const navigate = useNavigate()
  const routeNavigate = Route.useNavigate()
  const { tab } = Route.useSearch()
  const activeTab: InvoicesTab = tab ?? 'open'
  const [titleFilter, setTitleFilter] = useState('')
  const debouncedTitleFilter = useDebouncedValue(titleFilter.trim(), 300)
  const selectedOrganizationId = useStore(organizationStore, (s) => s.selectedOrganizationId)
  const { isConnected: isStripeConnected, hasLoaded: stripeHasLoaded } = useStripeConnection(selectedOrganizationId || '')

  // Redirect if Stripe is not connected (after stripe status loads)
  useEffect(() => {
    if (stripeHasLoaded && !isStripeConnected) {
      navigate({ to: '/dashboard' })
    }
  }, [navigate, isStripeConnected, stripeHasLoaded])

  useBreadcrumbFromRoute()

  const {
    invoices,
    totalCount,
    isLoading,
    hasLoaded,
    hasMore,
    fetchNextPage,
    isFetchingNextPage,
    isFetching,
    isPlaceholderData,
  } = useInfiniteInvoicesList({
    statuses: TAB_STATUSES[activeTab],
    titleFilter: debouncedTitleFilter || undefined,
  })
  const { stats, hasLoaded: statsHasLoaded } = useInvoiceStats()
  const isFilterLoading =
    titleFilter.trim() !== debouncedTitleFilter ||
    (isFetching && isPlaceholderData)
  const displayedInvoices = isFilterLoading || isPlaceholderData ? [] : invoices

  // Don't render if Stripe not connected or data not loaded yet
  if (!stripeHasLoaded || !isStripeConnected) {
    return <LoadingOverlay />
  }

  const handleTabChange = (value: InvoicesTab) => {
    routeNavigate({ search: { tab: value } })
  }

  if (isLoading || !hasLoaded) {
    return <LoadingOverlay message="Loading invoices..." />
  }

  return (
    <div className="flex h-full flex-col gap-4 p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="display-heading text-2xl font-semibold tracking-tight">Invoices</h1>
          <p className="text-sm text-muted-foreground">Manage open, overdue, and paid invoices.</p>
        </div>
        <ActionGate
          organizationId={selectedOrganizationId || ''}
          permission="InvoicesManage"
          verb="create"
          noun="invoices"
        >
          <Button asChild>
            <Link to="/invoices/new">
              <Plus className="h-4 w-4" />
              New Invoice
            </Link>
          </Button>
        </ActionGate>
      </div>

      {!statsHasLoaded ? (
        <div className="grid auto-rows-min gap-4 md:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-[104px] rounded-lg" />
          ))}
        </div>
      ) : stats ? (
        <div className="grid auto-rows-min gap-4 md:grid-cols-4">
          <DocumentStatCard
            label="Open"
            amountCents={Number(stats.openTotalCents)}
            count={stats.openCount}
            noun="invoice"
            isActive={activeTab === 'open'}
            onClick={() => handleTabChange('open')}
          />
          <DocumentStatCard
            label="Overdue"
            amountCents={Number(stats.overdueTotalCents)}
            count={stats.overdueCount}
            noun="invoice"
            isActive={activeTab === 'overdue'}
            onClick={() => handleTabChange('overdue')}
          />
          <DocumentStatCard
            label="Paid"
            amountCents={Number(stats.paidTotalCents)}
            count={stats.paidCount}
            noun="invoice"
            isActive={activeTab === 'paid'}
            onClick={() => handleTabChange('paid')}
          />
          <DocumentStatCard
            label="Cancelled"
            amountCents={Number(stats.cancelledTotalCents)}
            count={stats.cancelledCount}
            noun="invoice"
            isActive={activeTab === 'cancelled'}
            onClick={() => handleTabChange('cancelled')}
          />
        </div>
      ) : (
        <div className="grid auto-rows-min gap-4 md:grid-cols-4">
          <UnavailableStatCard
            label="Open"
            isActive={activeTab === 'open'}
            onClick={() => handleTabChange('open')}
          />
          <UnavailableStatCard
            label="Overdue"
            isActive={activeTab === 'overdue'}
            onClick={() => handleTabChange('overdue')}
          />
          <UnavailableStatCard
            label="Paid"
            isActive={activeTab === 'paid'}
            onClick={() => handleTabChange('paid')}
          />
          <UnavailableStatCard
            label="Cancelled"
            isActive={activeTab === 'cancelled'}
            onClick={() => handleTabChange('cancelled')}
          />
        </div>
      )}

      <div className="flex items-center justify-end">
        <DisputesBadge />
      </div>

      <InvoicesTable
        data={displayedInvoices}
        titleFilter={titleFilter}
        onTitleFilterChange={setTitleFilter}
        isFilterLoading={isFilterLoading}
        paginationResetKey={`${activeTab}:${debouncedTitleFilter}`}
      />

      <LoadMoreBar
        loaded={displayedInvoices.length}
        total={totalCount}
        hasMore={!isFilterLoading && !isPlaceholderData && hasMore}
        isLoading={isFetchingNextPage}
        onLoadMore={() => fetchNextPage()}
      />
    </div>
  )
}
